import { ScheduleOfValues } from 'ontraccr-common';

import { currencyFormatter } from '../../../helpers/inputParsers';

import { getNumberText } from '../helpers';

export const LANDSCAPE_MAX_VALUES = {
  xPosition: 780,
  yPosition: 560,
  spacing: 500,
};

export const PORTRAIT_MAX_VALUES = {
  xPosition: 500,
  yPosition: 780,
  spacing: 500,
};

export const roundToCents = (value) => Math.round(value * 100) / 100;

/**
 * Calculate dynamic font size based on currency string length
 * to prevent wrapping in PDF cells
 */
export const getCurrencyFontSize = (formattedCurrency) => {
  const length = formattedCurrency?.length || 0;
  if (length > 16) return 7;
  return 8; // default size
};

export const getBase64ImageFromURL = (url) => new Promise((resolve, reject) => {
  const img = document.createElement('img');
  img.setAttribute('crossOrigin', 'anonymous');
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const dataURL = canvas.toDataURL('image/png');
    resolve(dataURL);
  };
  img.onerror = (error) => {
    reject(error);
  };
  img.src = url && url instanceof File ? URL.createObjectURL(url) : url;
});

export const calculateTextWidth = (text, fontSize = 10) => {
  if (!text) return 0;
  if (text.length >= 30) return text.length * fontSize * 0.5;
  return text.length * fontSize * 0.6;
};

export const getTableRow = (row, includeNumberCol = false, pdfColumns = []) => {
  const pdfColumnsSet = new Set(pdfColumns);
  const rows = [{
    text: getNumberText(row),
    style: 'tableCell',
    alignment: 'center',
  }];
  if (includeNumberCol) {
    rows.push({ text: row.number ?? '', style: 'tableCell' });
  }

  const contractAmountText = currencyFormatter(row.contractAmount);
  const progressToDateText = currencyFormatter(row.progressToDate);
  const previousBillingsText = currencyFormatter(row.previousBillings);
  const invoiceAmountText = currencyFormatter(row.invoiceAmount);

  rows.push(
    { text: row.description, style: 'tableCell' },
    {
      text: contractAmountText,
      style: 'tableCell',
      fontSize: getCurrencyFontSize(contractAmountText),
      noWrap: true,
    },
    {
      text: `${(row.percentageComplete * 100).toFixed(2)}%`,
      style: 'tableCell',
      alignment: 'center',
    },
    {
      text: progressToDateText,
      style: 'tableCell',
      fontSize: getCurrencyFontSize(progressToDateText),
      noWrap: true,
    },
    {
      text: previousBillingsText,
      style: 'tableCell',
      fontSize: getCurrencyFontSize(previousBillingsText),
      noWrap: true,
    },
    {
      text: invoiceAmountText,
      style: 'tableCell',
      fontSize: getCurrencyFontSize(invoiceAmountText),
      noWrap: true,
    },
  );

  if (pdfColumnsSet.has('unitType')) {
    rows.push({ text: row.unitType ?? '', style: 'tableCell' });
  }
  if (pdfColumnsSet.has('estimatedQuantity')) {
    rows.push({ text: String(row.estimatedQuantity ?? ''), style: 'tableCell' });
  }
  if (pdfColumnsSet.has('previouslyBilledQuantity')) {
    rows.push({ text: String(row.previouslyBilledQuantity ?? ''), style: 'tableCell' });
  }
  if (pdfColumnsSet.has('billableQuantity')) {
    rows.push({ text: String(row.billableQuantity ?? ''), style: 'tableCell' });
  }
  if (pdfColumnsSet.has('completedQuantity')) {
    rows.push({ text: String(row.completedQuantity ?? ''), style: 'tableCell' });
  }
  if (pdfColumnsSet.has('quantityToDate')) {
    rows.push({ text: String(row.quantityToDate ?? ''), style: 'tableCell' });
  }
  if (pdfColumnsSet.has('quantityRemaining')) {
    const remaining = ScheduleOfValues.getQuantityRemaining(row);
    rows.push({
      text: String(remaining),
      style: 'tableCell',
      color: remaining < 0 ? 'red' : undefined,
    });
  }
  if (pdfColumnsSet.has('billingCode')) {
    rows.push({ text: row.billingCode ?? '', style: 'tableCell' });
  }

  return rows;
};

/**
 * Expand tree rows into flat PDF rows: a bold rollup parent followed by its
 * indented sub-items. Rows without sub-items are unchanged.
 */
export const getSovTableRows = (rows = [], includeNumberCol = false, pdfColumns = []) => {
  const descriptionIndex = includeNumberCol ? 2 : 1;

  return ScheduleOfValues.flattenSovTree(rows).map((row) => {
    const tableRow = getTableRow(row, includeNumberCol, pdfColumns);

    if (row.hasSubItems) return tableRow.map((cell) => ({ ...cell, bold: true }));

    if (ScheduleOfValues.isSubItem(row)) {
      tableRow[descriptionIndex] = {
        ...tableRow[descriptionIndex],
        margin: [10, 0, 0, 0],
      };
    }

    return tableRow;
  });
};

export const getSubContractCOHeader = (text, numCols = 8) => [{
  text,
  style: 'tableHeader',
  alignment: 'left',
  colSpan: numCols,
}];

export const getChangeOrderSummary = (summaryRow, includeEmpty = false, pdfColumns = []) => {
  const totalChangesText = currencyFormatter(summaryRow.totalChanges);
  const progressToDateText = currencyFormatter(summaryRow.changeOrderProgressToDate);
  const previousBillingsText = currencyFormatter(summaryRow.changeOrderPreviousBillings);
  const invoiceAmountText = currencyFormatter(summaryRow.changeOrderInvoiceAmount);

  const result = [
    {
      text: 'TOTAL CHANGES',
      style: 'tableHeader',
      alignment: 'right',
      colSpan: 2,
    },
    {},
    ...includeEmpty
      ? [{}] : [],
    {
      text: totalChangesText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(totalChangesText),
      noWrap: true,
    },
    {
      text: `${(
        summaryRow.totalChanges
          ? ((summaryRow.changeOrderProgressToDate / summaryRow.totalChanges) * 100)
          : 0
      ).toFixed(2)}%`,
      style: 'tableHeader',
    },
    {
      text: progressToDateText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(progressToDateText),
      noWrap: true,
    },
    {
      text: previousBillingsText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(previousBillingsText),
      noWrap: true,
    },
    {
      text: invoiceAmountText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(invoiceAmountText),
      noWrap: true,
    },
  ];

  pdfColumns.forEach(() => result.push({ text: '' }));

  return result;
};

export const getNonHoldbackSummary = (summaryRow, pdfColumns = []) => {
  const totalNonHoldbackText = currencyFormatter(summaryRow.totalNonHoldbackAmount);
  const progressToDateText = currencyFormatter(summaryRow.nonHoldbackProgressToDate);
  const previousBillingsText = currencyFormatter(summaryRow.nonHoldbackPreviousBillings);
  const invoiceAmountText = currencyFormatter(summaryRow.nonHoldbackInvoiceAmount);

  const result = [
    {
      text: 'TOTAL NON-HOLDBACK',
      style: 'tableHeader',
      alignment: 'right',
      colSpan: 2,
    },
    {},
    {
      text: totalNonHoldbackText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(totalNonHoldbackText),
      noWrap: true,
    },
    {
      text: `${(
        summaryRow.totalNonHoldbackAmount
          ? ((summaryRow.nonHoldbackProgressToDate / summaryRow.totalNonHoldbackAmount) * 100)
          : 0
      ).toFixed(2)}%`,
      style: 'tableHeader',
    },
    {
      text: progressToDateText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(progressToDateText),
      noWrap: true,
    },
    {
      text: previousBillingsText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(previousBillingsText),
      noWrap: true,
    },
    {
      text: invoiceAmountText,
      style: 'tableHeader',
      alignment: 'left',
      fontSize: getCurrencyFontSize(invoiceAmountText),
      noWrap: true,
    },
  ];

  pdfColumns.forEach(() => result.push({ text: '' }));

  return result;
};

/**
 * Build table column widths dynamically based on whether a Number column is included
 * and how many optional columns are selected.
 */
export const buildTableWidths = (includeNumberCol, numOptional) => {
  if (numOptional === 0) {
    return includeNumberCol
      ? ['7%', '8%', '25%', '12%', '14%', '12%', '11%', '11%']
      : ['7%', '27%', '13%', '14%', '13%', '13%', '13%'];
  }

  // When optional columns are present use auto widths so Description takes remaining space
  const optionalWidths = new Array(numOptional).fill('auto');

  if (includeNumberCol) {
    return ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', ...optionalWidths];
  }
  return ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', ...optionalWidths];
};

export const getBasePdfDefinition = (pageOrientation = 'portrait') => ({
  pageMargins: [20, 20, 20, 20],
  pageOrientation,
  styles: {
    tableHeader: {
      bold: true,
      fontSize: 8,
      color: 'black',
      alignment: 'center',
    },
    tableCell: {
      fontSize: 8,
    },
    pdfHeader: {
      marginBottom: 30,
    },
    pdfHeaderTable: {
      marginLeft: 50,
    },
    pdfFooter: {
      fontSize: 10,
    },
  },
});
